import {
  Link,
  Badge,
  MaskCpf,
  Header,
  Button,
  BreadCrumb,
  ApiLoadingSpinner,
} from "@funcional/react-ui";
import { IntlDateTime, IntlEnum } from "@funcional/react-intl-ui";
import { useFailedTransactions } from "@pbm/transactions/hooks";
import { useMemo, useState } from "react";
import { IFailedTransaction } from "@pbm/transactions/contracts";
import { Column } from "react-table";
import { useHistory } from "react-router-dom";
import {
  DefaultColumnFilter,
  GridView,
  IFilteredColumn,
  SelectColumnFilter,
} from "@funcional/react-table-ui";
import { ProgramCodeCell } from "../components/ProgramCodeCell";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useJwtUser } from "@funcional/react-auth-ui";
import { useExportFailedTransactions } from "../hooks/useExportFailedTransactions";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

interface CellProps {
  value: any;
  row: { original: IFailedTransaction };
}

const translateTransactionStatusColor = (
  type: string
): "success" | "danger" | "warning" | "default" => {
  return ({
    VALID: "success",
    INVALID: "danger",
  }[type] || "default") as "success" | "danger" | "warning" | "default";
};

export default function ListFailedTransactionPage() {
  const history = useHistory();
  const [displayFormFieldModal, setDisplayFormFieldModal] = useState(false);
  const [messageErroExport, setMessageErroExport] = useState("");
  const [exportFilters, setExportFilters] = useState<IFilteredColumn[]>([]);
  const [exceededExport, setExceededExport] = useState(false);
  const { user } = useJwtUser();
  const { mutateAsync, isLoading, isSuccess, isError } =
    useExportFailedTransactions(exportFilters);

  const onClickHandler = async () => {
    openModal();
    const result = await mutateAsync(exportFilters);
    if (result.exceeded) {
      if (result.message) setMessageErroExport(result?.message[0]);
    }
    setExceededExport(result.exceeded);
  };

  const openModal = () => {
    setDisplayFormFieldModal(true);
  };

  const closeModal = () => {
    setDisplayFormFieldModal(false);
  };
  function exportFailedTransactionalFilters(filters: IFilteredColumn[]) {
    setExportFilters(filters);
  }

  const showRecord = (row: any) =>
    history.push(`/pbm/failed-transactions/${row.original.id}`);
  const FastEditionTooltip = {
    fontSize: "12px",
    maxWidth: 500,
  };
  const columns: Column<IFailedTransaction>[] = useMemo(
    () => [
      {
        Header: "Programa(s)",
        accessor: "items",
        Filter: DefaultColumnFilter,
        Cell: ({ row: { original } }: CellProps) => (
          <ProgramCodeCell original={original} />
        ),
      },
      {
        //Header: "ID",
        Header: () => (
          <>
            <span>ID</span>
            <Tooltip
              title={
                <span style={FastEditionTooltip}>
                  Esse campo não possui busca parcial favor inserir todos os
                  dados para consulta.
                </span>
              }
              placement="right"
            >
              <InfoOutlinedIcon
                style={{ fontSize: "1.2em", verticalAlign: "top" }}
              />
            </Tooltip>
          </>
        ),
        accessor: "id",
        Filter: DefaultColumnFilter,
        Cell: ({ value, row: { original } }: CellProps) => (
          <Link to={`/pbm/failed-transactions/${original.id}`}>{value}</Link>
        ),
      },
      {
        Header: "№ Aut.",
        Filter: DefaultColumnFilter,
        accessor: "legacyIdentifiers.authorizationId" as any,
        title: "Número da Autorização",
        Cell: ({ row: { original } }: CellProps) => {
          return original?.authorizationNumber || "N/A";
        },
      },
      {
        Header: "№ Seq.",
        Filter: DefaultColumnFilter,
        accessor: "legacyIdentifiers.sequenceId" as any,
        title: "Número Sequencial",
        Cell: ({ row: { original } }: CellProps) => {
          return original?.sequenceNumber || "N/A";
        },
      },
      { Header: "CNPJ", accessor: "storeCode", Filter: DefaultColumnFilter },
      {
        Header: "CPF",
        accessor: "customerCode",
        Filter: DefaultColumnFilter,
        Cell: ({ value, row: { original } }: CellProps) => (
          <Link to={`/pbm/beneficiaries/${original.beneficiary?.id}`}>
            <MaskCpf value={original.customerCode} />
          </Link>
        ),
      },
      {
        Header: "Status",
        Cell: () => (
          <Badge color={translateTransactionStatusColor("INVALID")}>
            <IntlEnum name="pre_transaction_status" value={"INVALID"} />
          </Badge>
        ),
      },
      {
        Header: "Tipo",
        accessor: "type",
        Filter: SelectColumnFilter,
        options: [
          { value: "PRE_TRANSACTION", description: "pré-venda" },
          { value: "TRANSACTION", description: "confirmação" },
          { value: "CANCELLATION", description: "cancelamento" },
        ],
        Cell: ({ value }) => (
          <IntlEnum name="failed_transaction_type" value={value} />
        ),
      },
      {
        Header: "Data",
        accessor: "createdAt",
        Cell: ({ value }) => <IntlDateTime value={value} />,
      },
    ],
    []
  );

  return (
    <>
      <Header
        title="Transações Inválidas"
        buttons={
          <Button onClick={onClickHandler} color="primary">
            Exportar (.csv)
          </Button>
        }
      >
        <BreadCrumb
          marginBottom={0}
          items={[{ label: "Transações Inválidas" }]}
        />
      </Header>

      <Dialog
        fullWidth
        open={displayFormFieldModal}
        onClose={() => setDisplayFormFieldModal(false)}
      >
        <DialogTitle>
          <div className="flex flex-row justify-between">
            <h2 className="mt-1 text-3xl font-bold text-gray-600">
              Exportação das Transações Inválidas
            </h2>
            <div>
              <IconButton aria-label="close" onClick={closeModal}>
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          {isLoading && <ApiLoadingSpinner message="Agendando a extração..." />}
          {!isLoading && !exceededExport && isSuccess ? (
            <>
              <label>
                A extração do relatório foi agendada e será enviada para o seu
                e-mail: <b>{user?.email}</b>
              </label>
            </>
          ) : (
            !isLoading &&
            exceededExport && (
              <>
                <b style={{ color: "red" }}>{messageErroExport}</b>
              </>
            )
          )}
          {isError && !isLoading ? (
            <>
              <b style={{ color: "red" }}>
                Falha ao agendar a exportação, tente novamente mais tarde.
              </b>
            </>
          ) : null}
        </DialogContent>

        <DialogActions>&nbsp;</DialogActions>
      </Dialog>
      <GridView<IFailedTransaction>
        paginated
        showRecord={showRecord}
        columns={columns}
        hook={useFailedTransactions}
        onFilterChange={exportFailedTransactionalFilters}
      />
    </>
  );
}
